import { useState, useEffect } from 'react'
import { Button, Chip } from '@heroui/react'

import { useGifs } from '../../gifs/hooks'
import { gifDisplayChips } from '../../gifs/types'
import type { Gif } from '../../gifs/types'

import { GifThumbnail } from './GifThumbnail'

interface SearchCandidatesPreviewProps {
  candidateIds: string[]
  returnedGifIds?: string[]
  className?: string
}

interface CandidateAnalytics {
  totalCandidates: number
  loadedGifs: number
  breadthTagCounts: Record<string, number>
  ratingCounts: Record<string, number>
}

export function SearchCandidatesPreview({
  candidateIds,
  returnedGifIds = [],
  className,
}: SearchCandidatesPreviewProps) {
  const [currentPage, setCurrentPage] = useState(0)
  const [selectedCandidates, setSelectedCandidates] = useState<Gif[]>([])
  const [returnedGifs, setReturnedGifs] = useState<Gif[]>([])
  const [candidateAnalytics, setCandidateAnalytics] =
    useState<CandidateAnalytics | null>(null)
  const [returnedAnalytics, setReturnedAnalytics] =
    useState<CandidateAnalytics | null>(null)

  const { gifs, loading, error } = useGifs() // Get all GIFs from the hook
  const ITEMS_PER_PAGE = 16

  // Filter GIFs that match our candidate IDs
  useEffect(() => {
    if (candidateIds.length > 0 && gifs.length > 0) {
      const matchedGifs = candidateIds
        .map((id) => gifs.find((gif) => gif.id === id))
        .filter((gif): gif is Gif => gif !== undefined)

      setSelectedCandidates(matchedGifs)
    }
  }, [candidateIds, gifs])

  // Filter GIFs that match our returned GIF IDs
  useEffect(() => {
    if (returnedGifIds.length > 0 && gifs.length > 0) {
      const matchedReturnedGifs = returnedGifIds
        .map((id) => gifs.find((gif) => gif.id === id))
        .filter((gif): gif is Gif => gif !== undefined)

      setReturnedGifs(matchedReturnedGifs)
    }
  }, [returnedGifIds, gifs])

  // Calculate analytics for search candidates
  const calculateAnalytics = (gifsToAnalyze: Gif[]): CandidateAnalytics => {
    const breadthTagCounts: Record<string, number> = {}
    const ratingCounts: Record<string, number> = {}

    gifsToAnalyze.forEach((gif) => {
      // Breadth tags (retrievalSearch, legacy fallback)
      gifDisplayChips(gif).forEach((tag) => {
        breadthTagCounts[tag] = (breadthTagCounts[tag] || 0) + 1
      })

      // Rating
      const rating = gif.explicitRating
      ratingCounts[rating] = (ratingCounts[rating] || 0) + 1
    })

    return {
      totalCandidates: gifsToAnalyze.length,
      loadedGifs: gifsToAnalyze.length,
      breadthTagCounts,
      ratingCounts,
    }
  }

  useEffect(() => {
    if (selectedCandidates.length > 0) {
      setCandidateAnalytics({
        ...calculateAnalytics(selectedCandidates),
        totalCandidates: candidateIds.length,
        loadedGifs: selectedCandidates.length,
      })
    } else {
      setCandidateAnalytics(null)
    }
  }, [selectedCandidates, candidateIds.length])

  useEffect(() => {
    if (returnedGifs.length > 0) {
      setReturnedAnalytics(calculateAnalytics(returnedGifs))
    } else {
      setReturnedAnalytics(null)
    }
  }, [returnedGifs])

  if (candidateIds.length === 0) {
    return (
      <div className={`text-center py-4 ${className}`}>
        <div className="text-slate-500 text-sm">No search candidates found</div>
      </div>
    )
  }

  const totalPages = Math.ceil(candidateIds.length / ITEMS_PER_PAGE)
  const startIndex = currentPage * ITEMS_PER_PAGE
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, candidateIds.length)
  const currentPageIds = candidateIds.slice(startIndex, endIndex)
  const currentPageGifs = currentPageIds
    .map((id) => selectedCandidates.find((gif) => gif.id === id))
    .filter((gif): gif is Gif => gif !== undefined)

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1)
    }
  }

  const renderAnalyticsSection = (
    analytics: CandidateAnalytics,
    title: string,
    color: 'primary' | 'secondary' = 'primary'
  ) => (
    <div className="mb-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
      <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
        {title}
      </div>

      {/* Breadth Tag Distribution */}
      <div className="mb-3">
        <div className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">
          Breadth Tags ({Object.keys(analytics.breadthTagCounts).length} types):
        </div>
        <div className="flex flex-wrap gap-1">
          {Object.entries(analytics.breadthTagCounts)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 12)
            .map(([tag, count]) => (
              <Chip key={tag} size="sm" variant="flat" color={color}>
                {tag}: {count}
              </Chip>
            ))}
        </div>
      </div>

      {/* Rating Distribution */}
      <div className="text-xs">
        <div className="font-medium text-slate-600 dark:text-slate-400 mb-1">
          Rating:
        </div>
        <div className="flex flex-wrap gap-3">
          {Object.entries(analytics.ratingCounts)
            .sort(([, a], [, b]) => b - a)
            .map(([rating, count]) => (
              <div key={rating} className="flex gap-1">
                <span className="uppercase">{rating}:</span>
                <span className="font-medium">{count}</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  )

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-600 dark:text-slate-400">
            Search Candidates
          </span>
          <Chip size="sm" variant="flat" color="secondary">
            {candidateIds.length} total
          </Chip>
          <Chip size="sm" variant="flat" color="primary">
            {selectedCandidates.length} loaded
          </Chip>
          {returnedGifIds.length > 0 && (
            <Chip size="sm" variant="flat" color="success">
              {returnedGifIds.length} returned
            </Chip>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="flat"
              onPress={handlePrevPage}
              isDisabled={currentPage === 0}
            >
              Previous
            </Button>
            <span className="text-xs text-slate-500">
              Page {currentPage + 1} of {totalPages}
            </span>
            <Button
              size="sm"
              variant="flat"
              onPress={handleNextPage}
              isDisabled={currentPage >= totalPages - 1}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      {/* Analytics Sections */}
      {candidateAnalytics &&
        renderAnalyticsSection(candidateAnalytics, 'All Search Candidates')}

      {returnedAnalytics &&
        renderAnalyticsSection(
          returnedAnalytics,
          'Returned/Ranked GIFs',
          'secondary'
        )}

      {/* Loading/Error States */}
      {loading && (
        <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 dark:bg-blue-950 dark:border-blue-800 dark:text-blue-300">
          Loading GIF collection...
        </div>
      )}

      {error && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 dark:bg-red-950 dark:border-red-800 dark:text-red-300">
          Error loading GIFs: {error}
        </div>
      )}

      {/* Missing GIFs info */}
      {!loading && selectedCandidates.length < candidateIds.length && (
        <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700 dark:bg-yellow-950 dark:border-yellow-800 dark:text-yellow-300">
          Note: {candidateIds.length - selectedCandidates.length} GIFs not found
          in current collection (Total loaded: {gifs.length})
        </div>
      )}

      {/* Thumbnails Grid */}
      <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
        {currentPageGifs.map((gif) => (
          <GifThumbnail key={gif.id} gif={gif} />
        ))}

        {/* Show placeholder for IDs that don't have loaded GIFs */}
        {currentPageIds.slice(currentPageGifs.length).map((id) => (
          <div
            key={`missing-${id}`}
            className="relative aspect-square bg-slate-200 dark:bg-slate-700 rounded overflow-hidden flex items-center justify-center"
          >
            <div className="text-slate-500 text-xs font-mono text-center px-1">
              {id.slice(-8)}
              <div className="text-xs mt-1">Not found</div>
            </div>
          </div>
        ))}
      </div>

      {/* Page info */}
      {totalPages > 1 && (
        <div className="mt-3 text-center">
          <span className="text-xs text-slate-500">
            Showing {startIndex + 1}-{Math.min(endIndex, candidateIds.length)}{' '}
            of {candidateIds.length} candidates
          </span>
        </div>
      )}
    </div>
  )
}
